import React, { useState, useLayoutEffect, useEffect, useRef, useCallback } from "react";
import { Box, Typography } from "@mui/material";

function SeccionEspecialidades({ profesionalInfo }) {
  const [expandido, setExpandido] = useState(false);
  const [visibleCount, setVisibleCount] = useState(null); // null = midiendo
  const wrapperRef = useRef(null);
  const measureRef = useRef(null);
  const buttonMeasureRef = useRef(null);

  // Obtener especialidades ya procesadas desde el backend
  let especialidades = [];
  if (profesionalInfo?.perfil?.especialidades) {
    especialidades = Array.isArray(profesionalInfo.perfil.especialidades) 
      ? profesionalInfo.perfil.especialidades 
      : [];
  }

  // Función de medición
  const measureAndCalculate = useCallback(() => {
    const measureContainer = measureRef.current;
    const wrapper = wrapperRef.current;
    const buttonMeasure = buttonMeasureRef.current;

    if (!measureContainer || !wrapper || !buttonMeasure || especialidades.length === 0) {
      return;
    }

    // Forzar el ancho del contenedor de medición al ancho real del wrapper
    const wrapperWidth = wrapper.offsetWidth;
    measureContainer.style.width = `${wrapperWidth}px`;

    // Obtener todos los hijos (solo especialidades, sin botón)
    const children = Array.from(measureContainer.children);

    if (children.length === 0) {
      setVisibleCount(0);
      return;
    }

    // Mapear posiciones de cada elemento
    const positions = children.map((child, index) => ({
      index,
      top: child.offsetTop,
      left: child.offsetLeft,
      width: child.offsetWidth,
      height: child.offsetHeight,
    }));

    // Agrupar por filas (tolerancia de 5px)
    const rowMap = new Map();
    positions.forEach((pos) => {
      let foundRow = null;
      for (const [rowTop] of rowMap) {
        if (Math.abs(rowTop - pos.top) <= 5) {
          foundRow = rowTop;
          break;
        }
      }
      if (foundRow !== null) {
        rowMap.get(foundRow).push(pos);
      } else {
        rowMap.set(pos.top, [pos]);
      }
    });

    // Ordenar filas por posición Y
    const sortedRows = Array.from(rowMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map((entry) => entry[1]);

    // Si hay 2 filas o menos, todas las especialidades caben
    if (sortedRows.length <= 2) {
      setVisibleCount(especialidades.length);
      return;
    }

    // Contar elementos en las primeras 2 filas
    const row1 = sortedRows[0] || [];
    const row2 = sortedRows[1] || [];
    const elementsInTwoRows = [...row1, ...row2];
    const totalInTwoRows = elementsInTwoRows.length;

    // Calcular cuántas especialidades quedarían ocultas si mostramos todas las de 2 filas
    const hiddenIfShowAll = especialidades.length - totalInTwoRows;

    // Si no hay ocultas, mostrar todo
    if (hiddenIfShowAll <= 0) {
      setVisibleCount(especialidades.length);
      return;
    }

    // Obtener el último elemento de la fila 2
    const lastElementInRow2 = row2.length > 0 
      ? row2[row2.length - 1] 
      : row1[row1.length - 1];

    // Calcular espacio disponible al final de la fila 2
    const lastElementEnd = lastElementInRow2.left + lastElementInRow2.width;
    const gap = 8; // gap de MUI gap={1} = 8px
    const availableSpace = wrapperWidth - lastElementEnd - gap;

    // Medir el ancho del botón con el texto correcto
    // Actualizar el texto del botón mock para medir con el número correcto
    buttonMeasure.textContent = `${hiddenIfShowAll} más`;
    const buttonWidth = buttonMeasure.offsetWidth;

    // ¿Cabe el botón en el espacio disponible?
    if (buttonWidth <= availableSpace) {
      // ¡Sí cabe! No eliminamos ninguna especialidad
      setVisibleCount(totalInTwoRows);
    } else {
      // No cabe, necesitamos quitar la última especialidad
      // Pero verificamos si quitando una, el botón cabe
      const newHiddenCount = hiddenIfShowAll + 1;
      buttonMeasure.textContent = `${newHiddenCount} más`;
      
      // Ahora el botón debería caber en el espacio que dejó el elemento eliminado
      setVisibleCount(totalInTwoRows - 1);
    }
  }, [especialidades]);

  // Medir después del primer render y en cada cambio
  useLayoutEffect(() => {
    const timer = requestAnimationFrame(() => {
      measureAndCalculate();
    });
    return () => cancelAnimationFrame(timer);
  }, [measureAndCalculate]);

  // Usar ResizeObserver para detectar cambios de tamaño
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const resizeObserver = new ResizeObserver(() => {
      measureAndCalculate();
    });

    resizeObserver.observe(wrapper);

    return () => {
      resizeObserver.disconnect();
    };
  }, [measureAndCalculate]);

  // No renderizar si no hay especialidades
  if (!especialidades || especialidades.length === 0) {
    return null;
  }

  const handleToggle = () => {
    setExpandido(!expandido);
  };

  // Calcular si necesitamos botón
  const needsButton = visibleCount !== null && visibleCount < especialidades.length;
  const hiddenCount = visibleCount !== null ? especialidades.length - visibleCount : 0;

  // Estilos para las pastillas
  const pillBaseStyle = {
    fontFamily: "Quicksand-Regular",
    fontSize: { md: "16px", xs: "14px" },
    borderRadius: 2,
    px: 1.5,
    py: 0.5,
    whiteSpace: "nowrap",
  };

  const especialidadStyle = {
    ...pillBaseStyle,
    backgroundColor: "#ffffff",
    border: "1px solid #E0E0E0",
    color: "#09090a",
  };

  const buttonStyle = {
    ...pillBaseStyle,
    backgroundColor: "transparent",
    border: "1px solid #3B82F6",
    color: "#3B82F6",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "#2563EB",
      color: "#2563EB",
    },
    "&:active": {
      transform: "scale(0.98)",
    },
  };

  return (
    <Box ref={wrapperRef} sx={{ position: "relative" }}>
      <Typography
        sx={{
          mb: 2.5,
          mt: 2.5,
          fontFamily: "Quicksand-Bold",
          fontSize: { md: "20px", xs: "18px" },
        }}
      >
        Soy especialista en
      </Typography>

      {/* Contenedor de medición OCULTO - SOLO especialidades (sin botón) */}
      <Box
        ref={measureRef}
        aria-hidden="true"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          top: 0,
          left: 0,
          zIndex: -1,
        }}
      >
        {especialidades.map((especialidad, index) => (
          <Box key={`measure-${index}`} sx={especialidadStyle}>
            {especialidad}
          </Box>
        ))}
      </Box>

      {/* Botón de medición separado - para medir su ancho exacto */}
      <Box
        ref={buttonMeasureRef}
        aria-hidden="true"
        sx={{
          ...buttonStyle,
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          top: 0,
          left: 0,
          zIndex: -1,
        }}
      >
        99 más
      </Box>

      {/* Contenedor VISIBLE */}
      {visibleCount !== null && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
          }}
        >
          {/* Especialidades de las primeras 2 filas - SIEMPRE ESTÁTICAS */}
          {especialidades.slice(0, visibleCount).map((especialidad, index) => (
            <Box key={`visible-${index}`} sx={especialidadStyle}>
              {especialidad}
            </Box>
          ))}

          {/* Botón "n más" cuando está CONTRAÍDO */}
          {!expandido && needsButton && (
            <Box onClick={handleToggle} sx={buttonStyle}>
              {hiddenCount} más
            </Box>
          )}

          {/* Especialidades adicionales cuando está EXPANDIDO */}
          {expandido &&
            needsButton &&
            especialidades.slice(visibleCount).map((especialidad, index) => (
              <Box key={`extra-${index}`} sx={especialidadStyle}>
                {especialidad}
              </Box>
            ))}

          {/* Botón "Ver menos" cuando está EXPANDIDO */}
          {expandido && needsButton && (
            <Box onClick={handleToggle} sx={buttonStyle}>
              Ver menos
            </Box>
          )}
        </Box>
      )}

      {/* Loading state mientras mide */}
      {visibleCount === null && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: 1.5,
            visibility: "hidden",
          }}
        >
          {especialidades.slice(0, 3).map((especialidad, index) => (
            <Box key={`loading-${index}`} sx={especialidadStyle}>
              {especialidad}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default SeccionEspecialidades;
